import { useEffect, useRef, type ReactNode } from 'react';
import { Portal } from './Portal';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface OverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  backdropClass: string;
  panelClass: string;
  role?: 'dialog' | 'alertdialog';
  closeOnBackdrop?: boolean;
}

/**
 * Base commune de Modal et Drawer : portail, fond, fermeture par Échap,
 * piège à focus, restauration du focus et blocage du défilement.
 */
export function Overlay({
  open,
  onClose,
  children,
  labelledBy,
  backdropClass,
  panelClass,
  role = 'dialog',
  closeOnBackdrop = true,
}: OverlayProps) {
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus initial : premier élément focusable, sinon le panneau
    const first = panel.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <Portal>
      <div
        className={backdropClass}
        onMouseDown={(e) => {
          if (closeOnBackdrop && e.target === e.currentTarget) onClose();
        }}
      >
        <div ref={panel} role={role} aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} className={panelClass}>
          {children}
        </div>
      </div>
    </Portal>
  );
}
